import React, { useEffect, useState } from 'react';
import { useToast } from "@/hooks/use-toast";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { diagnoseApiKeys } from '@/utils/diagnostics';
import { getGoogleMapsApiKey, diagnoseGoogleMapsApiKey } from '@/utils/googleMapsKey';

interface GoogleMapLoaderProps {
  onMapsLoaded: () => void;
}

const GoogleMapLoader: React.FC<GoogleMapLoaderProps> = ({ onMapsLoaded }) => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [errorCount, setErrorCount] = useState(0);
  const [showDebugInfo, setShowDebugInfo] = useState(false);
  const [apiKeyError, setApiKeyError] = useState<string | null>(null);
  const [apiKeyAvailable, setApiKeyAvailable] = useState<boolean>(false);
  const [diagnosticsRun, setDiagnosticsRun] = useState(false);
  const [domainError, setDomainError] = useState<boolean>(false);
  const [currentDomain, setCurrentDomain] = useState<string>('');

  const runAdvancedDiagnostics = () => {
    console.group('🔍 Advanced Google Maps API Key Diagnostics');
    
    diagnoseGoogleMapsApiKey();
    
    const processedApiKey = getGoogleMapsApiKey();
    const rawApiKey = typeof import.meta !== 'undefined' && import.meta.env ? 
      import.meta.env.VITE_GOOGLE_MAPS_API_KEY : undefined;
    
    console.log('Raw key available in environment:', !!rawApiKey);
    console.log('Processed key available:', !!processedApiKey);
    console.log('Current domain:', window.location.hostname);
    
    if (rawApiKey) {
      console.log('Raw key length:', typeof rawApiKey === 'string' ? rawApiKey.length : 'Not a string');
      console.log('Raw key type:', typeof rawApiKey);
      if (typeof rawApiKey === 'string') {
        console.log('Raw key format check:', rawApiKey.includes('AIza') ? 'Contains AIza' : 'Missing AIza prefix');
        console.log('Raw key preview:', rawApiKey.substring(0, 10) + '...');
      }
    }
    
    if (processedApiKey) {
      console.log('Processed key length:', processedApiKey.length);
      console.log('Processed key format check:', processedApiKey.startsWith('AIza') ? 'Valid prefix' : 'Invalid prefix');
      console.log('Processed key preview:', `${processedApiKey.substring(0, 6)}...`);
    }
    
    console.log('Window.google available:', !!window.google);
    console.log('Document ready state:', document.readyState);
    console.log('User agent:', navigator.userAgent);
    console.log('Current host:', window.location.host);
    
    const diagnostics = diagnoseApiKeys();
    console.log('Diagnostic utility results:', diagnostics);
    
    console.groupEnd();
    setDiagnosticsRun(true);
    
    toast({
      title: "Diagnostics Complete",
      description: "Check the browser console for detailed API key information.",
      duration: 5000,
    });
  };

  useEffect(() => {
    setCurrentDomain(window.location.hostname);
    
    const handleGoogleMapsError = (event: ErrorEvent) => {
      if (event.filename?.includes('maps.googleapis.com')) {
        console.error('Google Maps API error detected:', event.message);
        
        if (event.message?.includes('RefererNotAllowedMapError')) {
          setDomainError(true);
          setApiKeyError(`Your Google Maps API key is not authorized for domain: ${window.location.hostname}. Please add this domain to your Google Cloud Console restrictions.`);
          
          toast({
            title: "Google Maps Domain Error",
            description: `This domain (${window.location.hostname}) is not authorized for your Google Maps API key.`,
            variant: "destructive",
          });
        }
      }
    };

    const originalConsoleError = console.error;
    console.error = function(msg, ...args) {
      if (typeof msg === 'string' && msg.includes('RefererNotAllowedMapError')) {
        setDomainError(true);
        setApiKeyError(`Your Google Maps API key is not authorized for domain: ${window.location.hostname}. Please add this domain to your Google Cloud Console restrictions.`);
      }
      originalConsoleError.apply(console, [msg, ...args]);
    };

    window.addEventListener('error', handleGoogleMapsError);
    
    return () => {
      window.removeEventListener('error', handleGoogleMapsError);
      console.error = originalConsoleError;
    };
  }, [toast]);

  useEffect(() => {
    if (window.google?.maps) {
      console.log("Google Maps API already loaded");
      if (!isLoaded) {
        setIsLoaded(true);
        onMapsLoaded();
      }
      return;
    }

    const apiKey = getGoogleMapsApiKey();
    
    console.log("Google Maps API Key Available:", apiKey ? "Yes" : "No");
    console.log("Google Maps API Key Length:", apiKey?.length || 0);
    console.log("Google Maps API Key Format Check:", apiKey?.startsWith('AIza') ? "Valid" : "Invalid");
    console.log("Current domain:", window.location.hostname);
    
    if (!apiKey || !apiKey.startsWith('AIza')) {
      console.error("Google Maps API Key appears to be in the wrong format. Should start with 'AIza'");
      console.log("Current key format:", apiKey ? (apiKey.substring(0, 4) + "...") : "API key is empty");
      
      diagnoseGoogleMapsApiKey();
    }
    
    setApiKeyAvailable(!!apiKey && apiKey.length > 0);

    if (isLoading || document.querySelector('script[src*="maps.googleapis.com/maps/api"]')) {
      return;
    }

    if (!window.initMap) {
      window.initMap = () => {
        console.log("Google Maps API loaded successfully");
        setIsLoaded(true);
        setIsLoading(false);
        onMapsLoaded();
      };
    }

    const existingScript = document.querySelector('script[src*="maps.googleapis.com/maps/api"]');
    if (!existingScript) {
      setIsLoading(true);
      
      console.log("Loading Google Maps with API key length:", apiKey?.length || 0);

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&callback=initMap&libraries=marker,places&loading=async`;
      script.async = true;
      script.defer = true;
      script.onerror = (event) => {
        console.error("Failed to load Google Maps API", event);
        setIsLoading(false);
        setErrorCount(prev => prev + 1);
        setApiKeyError("Failed to load Google Maps API. Your API key might be invalid or have incorrect permissions.");
        
        const errorMessage = errorCount > 0 
          ? "Multiple failures loading Google Maps. Please check your API key permissions and network connection."
          : "Failed to load Google Maps. Please check your API key and refresh.";
        
        toast({
          title: "Google Maps Error",
          description: errorMessage,
          variant: "destructive",
        });
      };
      
      const timeoutId = setTimeout(() => {
        if (!window.google?.maps) {
          console.warn("Google Maps loading timeout - might indicate an API key issue");
          
          if (isLoading && errorCount === 0) {
            toast({
              title: "Google Maps Loading Timeout",
              description: "Maps is taking longer than expected to load. This might indicate an API key issue.",
              variant: "destructive",
            });
          }
        }
      }, 10000);
      
      document.head.appendChild(script);
      
      return () => {
        clearTimeout(timeoutId);
      };
    }
    
    return () => {
      if (window.initMap) {
        window.initMap = () => {};
      }
    };
  }, [toast, onMapsLoaded, isLoading, isLoaded, errorCount]);

  return apiKeyError ? (
    <div className="mb-4">
      <Alert variant="destructive">
        <AlertTitle>{domainError ? "Google Maps Domain Restriction Error" : "Google Maps API Key Error"}</AlertTitle>
        <AlertDescription>
          {apiKeyError}
          <div className="mt-2">
            <button 
              onClick={() => setShowDebugInfo(!showDebugInfo)} 
              className="text-xs underline mr-2"
            >
              {showDebugInfo ? "Hide Debug Info" : "Show Debug Info"}
            </button>
            
            <Button 
              variant="outline" 
              size="sm" 
              onClick={runAdvancedDiagnostics}
              className="text-xs mt-2"
              disabled={diagnosticsRun}
            >
              {diagnosticsRun ? "Diagnostics Complete" : "Run Advanced Diagnostics"}
            </Button>
            
            {showDebugInfo && (
              <div className="mt-4 text-xs p-3 bg-gray-100 rounded">
                <p className="font-medium mb-2">Domain Restriction Error Details:</p>
                <p><strong>Current domain:</strong> {currentDomain}</p>
                
                {domainError && (
                  <div className="mt-3 bg-white p-3 rounded shadow-sm">
                    <p className="font-medium mb-1 text-red-600">How to fix this error:</p>
                    <ol className="list-decimal pl-5 space-y-2">
                      <li>Go to the <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Google Cloud Console</a></li>
                      <li>Select your project and find your API key</li>
                      <li>Click on the API key to edit its settings</li>
                      <li>Under "Application restrictions", select "HTTP referrers"</li>
                      <li>Add the following domains to the allowed list:
                        <div className="bg-gray-100 p-2 mt-1 rounded font-mono text-[10px] break-all">
                          <div>{currentDomain}/*</div>
                          <div>*.lovable.app/*</div>
                          <div>*.lovable.dev/*</div>
                          <div>*.lovableproject.com/*</div>
                        </div>
                      </li>
                      <li>Click Save, then refresh this page</li>
                    </ol>
                  </div>
                )}
              </div>
            )}
          </div>
        </AlertDescription>
      </Alert>
    </div>
  ) : null;
};

export default GoogleMapLoader;
